import { isRbacPermissionError } from '@squareup/dex-feature-shared-main-rbac-permission-check';
import {
  AccessTokenAPIError,
  AccessTokensData,
} from '@squareup/dex-store-access-shared-main-use-access-tokens-hook';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { ApiExplorerAccessTokenContext } from '@squareup/dex-ui-shared-api-explorer-providers';
import {
  Box,
  Heading10,
  Paragraph20,
  Paragraph30,
} from '@squareup/dex-ui-shared-base';
import {
  MarketDivider,
  MarketList,
  MarketRow,
} from '@squareup/dex-ui-shared-market';
import React, { FC, useMemo } from 'react';

import styles from './access-token-popover.module.css';

const strings = {
  accessTokens: 'Access tokens',
  noProdAccess: 'No access to production tokens for this account.',
  noSandboxAccess: 'No access to sandbox tokens for this account.',
  noTokens: 'No access tokens available.',
};

type Props = {
  accessTokenData: AccessTokensData[keyof AccessTokensData] | undefined;
  environment: ApplicationType;
  errors: AccessTokenAPIError[];
  onTokenSelect(_: ApiExplorerAccessTokenContext): void;
  selectedToken: string | undefined;
};

const PopoverContent: FC<Props> = ({
  accessTokenData,
  environment,
  errors,
  onTokenSelect,
  selectedToken,
}) => {
  const productionRowData = useMemo(
    () =>
      // Each production client has an access token
      accessTokenData?.accessTokens.map((accessToken) => {
        const client = accessTokenData.oauthClients.find(
          (client) => client.id === accessToken.client_id
        );
        return {
          id: String(accessToken.token_id),
          clientId: client?.id || '',
          name: client?.name || '',
          token: accessToken.token,
        };
      }) || [],
    [accessTokenData?.accessTokens, accessTokenData?.oauthClients]
  );

  const sandboxRowData = useMemo(() => {
    const sandboxData =
      accessTokenData as AccessTokensData[ApplicationType.Sandbox];
    // If the data is not available, return null
    if (!sandboxData?.merchants || !sandboxData?.authorizations) {
      return null;
    }
    // Map the data so that each client has a list of sandbox merchants with their access tokens
    return (
      sandboxData?.oauthClients
        .map((client) => {
          const clientAccessTokens = sandboxData?.merchants
            .map((merchant) => {
              if (merchant.is_primary) {
                const token = sandboxData?.accessTokens.find(
                  (token) =>
                    token.client_id === client.id &&
                    token.user_id === merchant.token
                );
                if (!token) {
                  return undefined;
                }
                return {
                  // The id is used for the key of the MarketRow
                  id: `${client.id}-${merchant.token}`,
                  clientId: client.production_client,
                  name: merchant.nickname,
                  token: token.token,
                };
              }
              const auth = sandboxData?.authorizations.find(
                (auth) =>
                  auth.client === client.id &&
                  auth.sandbox_merchant === merchant.token
              );
              if (!auth || !auth.access_token) {
                return undefined;
              }
              return {
                // The id is used for the key of the MarketRow
                id: `${client.id}-${merchant.token}`,
                clientId: client.production_client,
                name: merchant.nickname,
                token: auth.access_token,
              };
            })
            // Filter out merchants that have no access tokens
            .filter((token) => token !== undefined);
          return {
            client,
            tokens: clientAccessTokens,
          };
        })
        // Filter out clients that have no access tokens
        .filter((client) => client.tokens.length > 0) || []
    );
  }, [accessTokenData]);

  const hasRbacPermissionError = useMemo(() => {
    const error = errors.find(
      ({ endpoint, param }) =>
        param === environment &&
        ['getOauthClients', 'getAccessTokens'].includes(endpoint)
    );
    return error && isRbacPermissionError(error);
  }, [environment, errors]);

  function getAccessTokenDisplayValue(accessToken: string) {
    return accessToken
      ? `${accessToken.slice(0, 4)}...${accessToken.slice(-4)}`
      : '';
  }

  function renderTokenRow({
    id,
    name,
    token,
    clientId,
  }: {
    id: string;
    name: string;
    token: string;
    clientId: string;
  }) {
    return (
      <MarketRow
        className={styles['token-row']}
        interactive
        key={id}
        onClick={() =>
          onTokenSelect({ token, environment, productionClient: clientId })
        }
        {...(selectedToken && { selected: selectedToken === token })}
      >
        <Paragraph20 as="label" slot="label">
          {name}
        </Paragraph20>
        <Paragraph20 slot="subtext" colorVariant="20">
          {getAccessTokenDisplayValue(token)}
        </Paragraph20>
      </MarketRow>
    );
  }

  if (!accessTokenData || !environment) {
    return null;
  }

  if (environment === ApplicationType.Production) {
    if (productionRowData.length === 0) {
      return (
        <Box padding={{ vertical: '3x', horizontal: '4x' }}>
          <Heading10>{strings['accessTokens']}</Heading10>
          <Paragraph20>
            {hasRbacPermissionError
              ? strings['noProdAccess']
              : strings['noTokens']}
          </Paragraph20>
        </Box>
      );
    }
    return (
      <Box padding={{ vertical: '1x', horizontal: '3x' }}>
        <MarketList>
          {productionRowData.map((data) => renderTokenRow(data))}
        </MarketList>
      </Box>
    );
  }

  if (!sandboxRowData || sandboxRowData.length === 0) {
    return (
      <Box padding={{ vertical: '3x', horizontal: '4x' }}>
        <Heading10>{strings['accessTokens']}</Heading10>
        <Paragraph20>
          {hasRbacPermissionError
            ? strings['noSandboxAccess']
            : strings['noTokens']}
        </Paragraph20>
      </Box>
    );
  }

  return sandboxRowData?.map(({ client, tokens }, idx) => (
    <Box key={`container-${client.id}`}>
      {idx > 0 && <MarketDivider margin="small" size="thin" />}
      <Box padding={{ bottom: '0.5x', horizontal: '3x' }}>
        <Paragraph30 weight="semi-bold" padding={{ top: '1x', bottom: '1.5x' }}>
          {client.name}
        </Paragraph30>
        <MarketList>
          {tokens.map((data) => data !== undefined && renderTokenRow(data))}
        </MarketList>
      </Box>
    </Box>
  ));
};

export default PopoverContent;
